import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLocationDot } from '@fortawesome/free-solid-svg-icons/faLocationDot'
import { faSlash } from '@fortawesome/free-solid-svg-icons/faSlash'
import { getHistoryLabel } from '../../../../helpers/historyHelper.ts'
import { isPointLocationValid, isNoMotionReport } from '../../../../helpers/locationStatusHelper.ts'
import { effectiveTimezone } from '../../../../store/authState.ts'
import { useSnapshot } from 'valtio'
import { authState } from '../../../../store/authState.ts'
import { IPet, IPoint } from '../../../../interfaces/types.ts'
import { getNumberPawSvg } from '../../../map/components/icons.ts'
import { getTextColor } from '../../../../helpers/colorHelper.ts'

interface Props {
    location: IPoint
    pet: IPet
    index: number
    total: number
}

export const HistoryCard: React.FC<Props> = ({ location, pet, index, total }) => {
    // Subscribe to authState so timezone changes trigger re-render
    useSnapshot(authState)
    const zone = effectiveTimezone()?.value

    const dt = DateTime.fromISO(location?.properties?.receivedTime).setZone(zone)
    const time = dt.toLocaleString(DateTime.TIME_SIMPLE)
    const date = dt.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    const eventLabel = getHistoryLabel(location.properties.gibiMessageType, location.properties.status)
    const valid = isPointLocationValid(location)
    const noMotion = isNoMotionReport(location.properties)
    const tc = getTextColor(pet?.iconColor || '')
    const svg = getNumberPawSvg(pet?.iconColor || '', index + 1, tc === '!text-white' ? 'white' : 'black')

    const batteryText = location.properties.batteryPercent
        ? `${location.properties.batteryPercent}`
        : location.properties.battery || '--'

    return (
        <div
            className={`w-full rounded-xl shadow-md border-2 px-3 py-2 select-none
                ${!valid ? 'bg-gray-200 text-white border-gray-500' : 'bg-base-100 text-primary border-primary'}`}
        >
            {/* Main row */}
            <div className={'flex items-center gap-3'}>
                {/* Number badge */}
                <img className={'w-9 h-9 shrink-0'} src={svg} alt={String(index + 1)} />

                {/* Info */}
                <div className={'flex-1 min-w-0'}>
                    {/* Event label */}
                    <div className={'flex items-center gap-1.5'}>
                        {valid ? (
                            <FontAwesomeIcon
                                icon={faLocationDot}
                                className={'text-sm text-success'}
                            />
                        ) : (
                            <span className={'fa-stack'} style={{ fontSize: '0.9em', width: '1.5em', height: '1.5em', lineHeight: '1.5em' }}>
                                <FontAwesomeIcon icon={faLocationDot} className={'fa-stack-1x text-gray-400'} />
                                <FontAwesomeIcon icon={faSlash} className={'fa-stack-1x text-gray-400'} />
                            </span>
                        )}
                        <span
                            className={`badge badge-sm font-semibold ${noMotion ? 'badge-accent text-accent-content' : !valid ? 'bg-gray-500 text-white border-white/40' : 'badge-primary text-primary-content'}`}
                        >
                            {eventLabel}
                        </span>
                    </div>
                    {/* Time + date */}
                    <div className={`text-sm mt-0.5 ${!valid ? 'text-gray-500' : 'text-base-content/70'}`}>
                        {time} &middot; {date}
                    </div>
                    {/* Battery */}
                    <div className={`text-xs mt-0.5 ${!valid ? 'text-gray-500' : 'text-primary'}`}>
                        Battery: {batteryText}
                    </div>
                </div>

                {/* Position counter */}
                <div className={'shrink-0'}>
                    <span className={`text-xs ${!valid ? 'text-gray-500' : 'text-base-content/50'}`}>
                        {index + 1} of {total}
                    </span>
                </div>
            </div>
        </div>
    )
}
